import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useUIStore } from '../../lib/uiStore';
import { useAuth } from '../../../contexts/AuthContext';
import {
  PanelLeftClose, PanelLeftOpen, Search, Sun, Moon,
  Bell, User, Settings, LogOut, ChevronDown, Sparkles,
} from 'lucide-react';

export function Header() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const theme = useUIStore((s) => s.theme);
  const toggleTheme = useUIStore((s) => s.toggleTheme);
  const openCommandPalette = useUIStore((s) => s.openCommandPalette);
  const toggleChat = useUIStore((s) => s.toggleChat);
  const chatOpen = useUIStore((s) => s.chatOpen);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSignOut = async () => {
    setUserMenuOpen(false);
    await signOut();
    navigate('/', { replace: true });
  };

  return (
    <header className="h-16 flex items-center gap-4 px-5 border-b border-border bg-surface-raised/78 backdrop-blur-2xl flex-shrink-0 sticky top-0 z-30">
      {/* Sidebar toggle */}
      <button
        onClick={toggleSidebar}
        className="p-2 rounded-2xl text-content-muted hover:text-content hover:bg-surface-overlay/90 transition-colors"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
      </button>

      {/* Search bar (fake — opens command palette) */}
      <button
        onClick={openCommandPalette}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-full border border-border',
          'bg-surface-raised/80 text-content-muted text-sm backdrop-blur-xl',
          'hover:border-primary-500/30 hover:bg-surface-overlay/90 transition-all shadow-subtle',
          'flex-1 max-w-md'
        )}
      >
        <Search className="h-4 w-4 flex-shrink-0" />
        <span className="flex-1 text-left truncate font-medium tracking-[-0.02em]">Search parts, BOMs, changes...</span>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded-full bg-surface-overlay px-2 py-1 text-[10px] font-mono text-content-muted border border-border-muted">
          ⌘K
        </kbd>
      </button>

      <div className="flex-1" />

      {/* Actions */}
      <div className="flex items-center gap-1">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-2xl text-content-muted hover:text-content hover:bg-surface-overlay/90 transition-colors"
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        {/* AI Chat */}
        <button
          onClick={toggleChat}
          className={cn(
            'p-2 rounded-2xl transition-colors relative',
            chatOpen ? 'bg-primary-500/10 text-primary-500' : 'text-content-muted hover:text-content hover:bg-surface-overlay'
          )}
          aria-label="AI Assistant"
          title="AI Assistant"
        >
          <Sparkles className="h-4 w-4" />
        </button>

        {/* Notifications */}
        <button
          className="p-2 rounded-2xl text-content-muted hover:text-content hover:bg-surface-overlay/90 transition-colors relative"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
        </button>

        {/* User menu */}
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 p-1.5 rounded-full hover:bg-surface-overlay/90 transition-colors border border-transparent hover:border-border"
          >
            <div className="h-7 w-7 rounded-full bg-primary-500/20 flex items-center justify-center">
              <span className="text-xs font-semibold text-primary-500">
                {user?.email?.[0]?.toUpperCase() || 'U'}
              </span>
            </div>
            <ChevronDown className={cn('h-3 w-3 text-content-muted transition-transform', userMenuOpen && 'rotate-180')} />
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-surface-raised/95 backdrop-blur-2xl border border-border rounded-[22px] shadow-elevated py-1 animate-slide-up z-50">
              <div className="px-3 py-2 border-b border-border">
                <p className="text-sm font-medium text-content truncate">{user?.email || 'User'}</p>
                <p className="text-xs text-content-muted">Engineer</p>
              </div>
              <button
                onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content-secondary hover:bg-surface-overlay hover:text-content transition-colors"
              >
                <User className="h-4 w-4" />
                Profile
              </button>
              <button
                onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content-secondary hover:bg-surface-overlay hover:text-content transition-colors"
              >
                <Settings className="h-4 w-4" />
                Settings
              </button>
              <div className="border-t border-border my-1" />
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger-500 hover:bg-danger-500/5 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
